import React, { createContext, useState, useContext } from 'react';
import { ethers } from 'ethers';
import config from '../config/network';

const Web3Context = createContext(null);

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [networkOk, setNetworkOk] = useState(true);

  async function switchNetwork() {
    if (!window.ethereum) return false;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: config.chainId }],
      });
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: config.chainId,
              chainName: config.chainName,
              rpcUrls: config.rpcUrls,
              blockExplorerUrls: config.blockExplorerUrls,
              nativeCurrency: config.nativeCurrency,
            }],
          });
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  }

  async function checkAndSwitchNetwork() {
    if (!window.ethereum) return false;
    try {
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== config.chainId) {
        setNetworkOk(false);
        const switched = await switchNetwork();
        if (switched) {
          setNetworkOk(true);
          return true;
        }
        return false;
      }
      setNetworkOk(true);
      return true;
    } catch {
      return false;
    }
  }

  async function connectWallet() {
    if (!window.ethereum) {
      return { success: false, error: 'No wallet found. Please install Trust Wallet or MetaMask.' };
    }
    setLoading(true);
    try {
      const netOk = await checkAndSwitchNetwork();
      if (!netOk) {
        setLoading(false);
        return { success: false, error: 'Please switch to the correct network.' };
      }

      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const addr = accounts[0];
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const web3Signer = web3Provider.getSigner();

      setAccount(addr);
      setProvider(web3Provider);
      setSigner(web3Signer);
      setNetworkOk(true);

      // Get userId and check admin
      const CoreABI = require('../abi/ClusterCore.json');
      const coreContract = new ethers.Contract(config.contracts.core, CoreABI, web3Provider);
      try {
        const uid = await coreContract.walletToUserId(addr);
        setUserId(uid.toString() === '0' ? null : uid.toString());

        // Check if connected wallet is owner or admin
        const [ownerAddr, adminAddr] = await Promise.all([
          coreContract.owner(),
          coreContract.admin(),
        ]);
        const addrLower = addr.toLowerCase();
        const isOwnerWallet = addrLower === ownerAddr.toLowerCase();
        const isAdminWallet = addrLower === adminAddr.toLowerCase();
        setIsOwner(isOwnerWallet);
        setIsAdmin(isOwnerWallet || isAdminWallet);
      } catch {
        setUserId(null);
        setIsAdmin(false);
      }

      setLoading(false);
      return { success: true };
    } catch (err) {
      setLoading(false);
      return { success: false, error: err.message || 'Connection failed' };
    }
  }

  function disconnectWallet() {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setUserId(null);
    setIsAdmin(false);
    setIsOwner(false);
  }

  // Listen for account and chain changes — no callbacks that cause loops
  // We use a one-time setup via React effect in App.js
  function setupListeners() {
    if (!window.ethereum) return;

    window.ethereum.on('accountsChanged', async (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else {
        const addr = accounts[0];
        setAccount(addr);
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
        setProvider(web3Provider);
        setSigner(web3Provider.getSigner());

        // Re-check userId and admin/owner for new wallet
        try {
          const CoreABI = require('../abi/ClusterCore.json');
          const coreContract = new ethers.Contract(config.contracts.core, CoreABI, web3Provider);
          const uid = await coreContract.walletToUserId(addr);
          setUserId(uid.toString() === '0' ? null : uid.toString());

          const [ownerAddr, adminAddr] = await Promise.all([
            coreContract.owner(),
            coreContract.admin(),
          ]);
          const addrLower = addr.toLowerCase();
          setIsOwner(addrLower === ownerAddr.toLowerCase());
          setIsAdmin(addrLower === ownerAddr.toLowerCase() || addrLower === adminAddr.toLowerCase());
        } catch {
          setUserId(null);
          setIsAdmin(false);
          setIsOwner(false);
        }
      }
    });

    window.ethereum.on('chainChanged', (chainId) => {
      if (chainId !== config.chainId) {
        setNetworkOk(false);
      } else {
        setNetworkOk(true);
        // Re-init provider without full page refresh
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
        setProvider(web3Provider);
        setSigner(web3Provider.getSigner());
      }
    });
  }

  async function refreshUserId() {
    if (!provider || !account) return;
    try {
      const CoreABI = require('../abi/ClusterCore.json');
      const coreContract = new ethers.Contract(config.contracts.core, CoreABI, provider);
      const uid = await coreContract.walletToUserId(account);
      setUserId(uid.toString() === '0' ? null : uid.toString());

      // Also re-check admin/owner
      const [ownerAddr, adminAddr] = await Promise.all([
        coreContract.owner(),
        coreContract.admin(),
      ]);
      const addrLower = account.toLowerCase();
      setIsOwner(addrLower === ownerAddr.toLowerCase());
      setIsAdmin(addrLower === ownerAddr.toLowerCase() || addrLower === adminAddr.toLowerCase());
    } catch {
      setUserId(null);
    }
  }

  return (
    <Web3Context.Provider value={{
      account,
      provider,
      signer,
      userId,
      isAdmin,
      isOwner,
      loading,
      networkOk,
      connectWallet,
      disconnectWallet,
      switchNetwork,
      checkAndSwitchNetwork,
      setupListeners,
      refreshUserId,
    }}>
      {children}
    </Web3Context.Provider>
  );
}

export function useWeb3() {
  const context = useContext(Web3Context);
  if (!context) throw new Error('useWeb3 must be used within Web3Provider');
  return context;
}
